import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { IconChevronDown } from "@pentabd/icons";
import {
  Avatar,
  Button,
  Header,
  InputText,
  Modal,
  Select,
  Text,
} from "@pentabd/ui";

import { useLanguage } from "@hooks/useLanguage";

import { getCommonInfoDataConvert } from "@helpers/commonInfoDataConvert";
import { getEmployeeServiceDataConvert } from "@helpers/employeeServiceDataConvert";

import {
  ApplyLeaveFormDataType,
  MODAL,
} from "@validations/HRM/leave/applyLeaveFormValidation";

import { DECISION_TYPE } from "@constants/workflow-decision-type";

const { APPROVE, REJECT } = DECISION_TYPE;

interface LeaveDetailsModalType {
  title: string;
  employeeName: string;
  isModalOpen: boolean;
  commonInfo: any[];
  designation: any[];
  positionId: number;
  closeModal: () => void;
}

export const LeaveDetailsModal = ({
  title,
  employeeName,
  isModalOpen,
  commonInfo,
  designation,
  positionId,
  closeModal,
}: LeaveDetailsModalType) => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const { register, setValue, control } =
    useFormContext<ApplyLeaveFormDataType>();

  return (
    <Modal
      isOpen={isModalOpen}
      overlay={isModalOpen}
      onClose={closeModal}
      closeAble
    >
      <Header
        size="sm"
        headerText={{
          header: title,
          subHeader: employeeName,
        }}
        avatar={<Avatar size="sm" />}
        className="px-9"
      />
      {title !== APPROVE && title !== REJECT ? (
        <div className="p-10 d-flex flex-column gap-8">
          <div className="row">
            <div className="col-3">
              <Text weight="semibold" color="title" size="sm" component="p">
                {t("LEAVE_APPLY.FORM.FIELDS_FOR_APPROVAL.DESIGNATION.LABEL")}
              </Text>
            </div>
            <div className="col-9">
              <Controller
                control={control}
                name={MODAL.DESIGNATION}
                render={({ field }) => {
                  return (
                    <Select
                      {...register(MODAL.DESIGNATION)}
                      options={getCommonInfoDataConvert(designation)}
                      placeholder={t(
                        "LEAVE_APPLY.FORM.FIELDS_FOR_APPROVAL.DESIGNATION.PLACEHOLDER",
                      )}
                      defaultValue={field.value as any}
                      onSelectItem={(e) =>
                        setValue(MODAL.DESIGNATION, e as any)
                      }
                      suffix={<IconChevronDown size="16" />}
                    />
                  );
                }}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-3">
              <Text weight="semibold" color="title" size="sm" component="p">
                {t("LEAVE_APPLY.FORM.FIELDS_FOR_APPROVAL.FILE_ON_HAND.LABEL")}
              </Text>
            </div>
            <div className="col-9">
              <Controller
                control={control}
                name={MODAL.FILE_ON_HAND}
                render={({ field }) => {
                  return (
                    <Select
                      {...register(MODAL.FILE_ON_HAND)}
                      options={getEmployeeServiceDataConvert(
                        commonInfo,
                        language,
                      )}
                      placeholder={t(
                        "LEAVE_APPLY.FORM.FIELDS_FOR_APPROVAL.FILE_ON_HAND.PLACEHOLDER",
                      )}
                      defaultValue={field.value as any}
                      onSelectItem={(e) =>
                        setValue(MODAL.FILE_ON_HAND, e as any)
                      }
                      disabled={!positionId}
                      suffix={positionId ? <IconChevronDown size="16" /> : ""}
                    />
                  );
                }}
              />
            </div>
          </div>
        </div>
      ) : (
        <></>
      )}
      <div className="px-10 py-9 border-bottom d-flex flex-column gap-6">
        <Text color="dark" weight="semibold">
          {t("LEAVE_APPLY.MODAL.APPROVE_COMMENT_LABEL.FIRST_PART")}
          <Text size="sm" color="subtitle3" weight="medium">
            &nbsp;
            {t("LEAVE_APPLY.MODAL.APPROVE_COMMENT_LABEL.SECOND_PART")}
          </Text>
        </Text>
        <InputText
          {...register(MODAL.COMMENT)}
          placeholder={t("LEAVE_APPLY.MODAL.APPROVE_COMMENT")}
        />
      </div>
      <div className="p-9 d-flex justify-content-end align-items-center gap-6">
        <Button size="sm" fill="outline" type="light" onClick={closeModal}>
          {t("LEAVE_APPLY.MODAL.BUTTON.CANCEL_BUTTON")}
        </Button>
        <Button size="sm" htmlType="submit" fill="fill" type="primary">
          {t("LEAVE_APPLY.MODAL.BUTTON.SUBMIT_BUTTON")}
        </Button>
      </div>
    </Modal>
  );
};
